import usePrivateAxios from "@/common/api/usePrivateAxios"
import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import TextField from "@/common/components/TextField"
import { useMutation } from "@tanstack/react-query"
import { ChevronsRight } from "lucide-react"
import { useParams } from "react-router-dom"
import { ProductCategoryValidationSchema, createSchema } from "../../schemas/create.schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { SubmitHandler, useForm } from "react-hook-form"
import { useState } from "react"
import useProductCategory from "../../hooks/useProductCategory"
import GoBackButton from "@/common/components/GoBackButton";
import useUserPermissions from "@/common/hooks/useUserPermissions"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import { useGoBack } from "@/common/hooks/useGoBack"

const Update = () => {
  const goBack = useGoBack();
  const privateAxios = usePrivateAxios();
  const [serverError, setServerError] = useState(false);
  const { id } = useParams();

  const {
    data: productCategory
  } = useProductCategory(id || '')

  const { mutate, isPending } = useMutation({
    mutationKey: ['updateProductCategory'],
    mutationFn: async (form: ProductCategoryValidationSchema) => privateAxios.put(`/product_categories/${id}`, { product_category: form }),
    onSuccess: () => {
      goBack({ fallback: `/product-categories/${id}` });
    },
    onError: () => {
      setServerError(true)
    }
  });

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasUpdateProductCategoryPermission = !isUserPermissionsPending ? hasUserPermission('Admin::ProductCategories::Update') : true

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ProductCategoryValidationSchema>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      display_name: productCategory?.display_name
    }
  })

  const onSubmit: SubmitHandler<ProductCategoryValidationSchema> = (data) => mutate(data);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasUpdateProductCategoryPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/product-categories/${id}`} />
            <p className="text-white-400 font-normal">Categoria de Produtos</p>
            <ChevronsRight color='#ffffffb3' />
            <h2>Nova categoria</h2>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => goBack({ fallback: `/product-categories/${id}` })}
              variant='outline-error'
            >
              Cancelar
            </Button>

            <Button
              data-testid="update_product_categories"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={isPending}
            >
              Confirmar
            </Button>
          </div>
        </div>
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {serverError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
          <p className="text-lg font-bold">Nova Categoria</p>
          <TextField
            label="Nome da categoria"
            defaultValue={productCategory?.display_name || ''}
            {...register('display_name')}
            errorMessage={errors?.display_name?.message || undefined}
            data-testid="display_name_input"
          />
        </Box>
      </form>
    </ErrorMessageWrapper>
  )
}

export default Update
